/**
 * Черновик крупного действия: что показать до подтверждения.
 *
 * Разбор живёт отдельно от компонента, потому что «что именно собирается
 * сделать модель» читается из аргументов вызова, а их форма — договор с
 * реестром инструментов бэкенда, а не с вёрсткой.
 */

/** Одна задача пачки в том виде, в каком её показывает таблица (макет 8-3). */
export interface DraftIssue {
  title: string
  /** Значения реестра: task, bug, story, epic, subtask. */
  type: string
  /** Значения реестра: lowest…highest. */
  priority: string
}

/**
 * Поле, которое переписывают (макет 8-4).
 *
 * `content_md` — тело страницы базы знаний: там переписывание такое же крупное
 * действие, как описание задачи, и показывается тем же сравнением.
 */
export interface DraftField {
  name: 'title' | 'description_md' | 'content_md'
  after: string
}

/**
 * Что нарисовать вместо аргументов вызова.
 *
 * Разбор идёт по имени инструмента, а не по форме аргументов: имена — тот же
 * договор, что и схема, и неизвестный инструмент обязан доехать до экрана
 * сырьём, а не пропасть. Пропавший черновик оставил бы разговор в тупике:
 * подтверждать нечего, а ход стоит и ждёт человека.
 */
export type DraftView =
  | {
      kind: 'issues'
      issues: DraftIssue[]
      epicKey: string
      projectKey: string
      /** Пачка правится построчно, одиночная задача — теми же полями (DEV-1562) */
      batch: boolean
    }
  /**
   * Переписывание текста. Правят либо задачу (`issueKey`), либо страницу базы
   * знаний (`pageId`) — сравнение рисуется одно и то же, разное только то,
   * откуда панель берёт «как было».
   */
  | { kind: 'fields'; issueKey: string; pageId: string; fields: DraftField[] }
  | { kind: 'order'; keys: string[] }
  | { kind: 'raw' }

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

const asText = (value: unknown): string => (typeof value === 'string' ? value : '')

const asIssue = (value: unknown): DraftIssue => {
  const item = asRecord(value)

  return {
    title: asText(item?.title),
    type: asText(item?.type) || 'task',
    priority: asText(item?.priority) || 'medium',
  }
}

/** Поля, переписывание которых бэкенд считает крупным действием. */
const LARGE_FIELDS: DraftField['name'][] = ['title', 'description_md']

/** Те же поля у страницы базы знаний: мелких полей у неё нет вовсе. */
const PAGE_FIELDS: DraftField['name'][] = ['title', 'content_md']

const changedFields = (input: Record<string, unknown>, names: DraftField['name'][]): DraftField[] =>
  names
    .filter((field) => typeof input[field] === 'string')
    .map((field) => ({ name: field, after: asText(input[field]) }))

export const describeDraft = (name: string, args: unknown): DraftView => {
  const input = asRecord(args) ?? {}

  if (name === 'create_issues_batch') {
    const issues = Array.isArray(input.issues) ? input.issues.map(asIssue) : []

    return {
      kind: 'issues',
      issues,
      epicKey: asText(input.epic_key),
      projectKey: asText(input.project),
      batch: true,
    }
  }

  if (name === 'create_issue') {
    return {
      kind: 'issues',
      issues: [asIssue(input)],
      epicKey: asText(input.parent_key),
      projectKey: asText(input.project),
      batch: false,
    }
  }

  // Перестановка плана присылает только порядок ключей: почему он такой,
  // модель объясняет словами над черновиком, а чем задача занята — панель
  // достаёт из плана, который у неё и так на экране (макет 9-4).
  if (name === 'reorder_stack') {
    const keys = Array.isArray(input.issues) ? input.issues.map(asText).filter(Boolean) : []

    if (keys.length) return { kind: 'order', keys }
  }

  if (name === 'update_issue') {
    const fields = changedFields(input, LARGE_FIELDS)

    // Мелкие поля крупным действием не бывают: приоритет и исполнителя бэкенд
    // применяет сразу. Если в вызове их нет — показывать нечего, и разбор
    // честнее отдать сырьём, чем нарисовать пустой diff.
    if (fields.length) {
      return { kind: 'fields', issueKey: asText(input.issue), pageId: '', fields }
    }
  }

  // Правка страницы базы знаний (DEV-1922). Тело инструмент заменяет целиком,
  // поэтому без сравнения черновик — это вся страница «добавлена», и по нему
  // не увидеть, что модель на самом деле поменяла.
  if (name === 'update_page') {
    const fields = changedFields(input, PAGE_FIELDS)

    if (fields.length) {
      return { kind: 'fields', issueKey: '', pageId: asText(input.id), fields }
    }
  }

  return { kind: 'raw' }
}

/**
 * Числа подряд — одним промежутком: «5–7», «2, 5–7».
 *
 * Черновик порядка перечисляет только переехавшие строки, а про остальные
 * говорит одной фразой (макет 9-4). Перечислять их поимённо нельзя: в плане из
 * тридцати задач модель двигает три, и подпись «без изменений» заняла бы больше
 * места, чем сама перестановка.
 */
export const compressRanges = (numbers: number[]): string => {
  const sorted = [...new Set(numbers)].sort((left, right) => left - right)
  const parts: string[] = []

  for (let index = 0; index < sorted.length; ) {
    const start = sorted[index] as number
    let end = start

    while (sorted[index + 1] === end + 1) {
      end += 1
      index += 1
    }

    index += 1
    parts.push(end > start ? `${start}–${end}` : String(start))
  }

  return parts.join(', ')
}

/** Строка сравнения: убранная или добавленная. Неизменённые не показываются. */
export interface DiffLine {
  added: boolean
  text: string
}

/**
 * Сравнение по строкам, сгруппированное по местам правки.
 *
 * Показываются только изменённые строки: описание задачи бывает на сотню строк,
 * и целиком оно вытеснит с экрана и сам разговор, и кнопки под черновиком.
 * Пропущенное между группами обозначается разрывом — так видно, что правка не
 * одна.
 *
 * Наибольшая общая подпоследовательность считается таблицей: описания короткие,
 * и сложность здесь дешевле неверного сравнения, при котором сдвиг на одну
 * строку красит в «изменилось» весь остаток текста.
 */
export const diffHunks = (before: string, after: string): DiffLine[][] => {
  const source = before ? before.split('\n') : []
  const target = after ? after.split('\n') : []

  const width = target.length + 1
  const common = new Int32Array((source.length + 1) * width)
  const at = (i: number, j: number): number => common[i * width + j] ?? 0

  for (let i = source.length - 1; i >= 0; i--) {
    for (let j = target.length - 1; j >= 0; j--) {
      common[i * width + j] =
        source[i] === target[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1))
    }
  }

  const hunks: DiffLine[][] = []
  let hunk: DiffLine[] = []

  // Разрыв закрывает группу: следующая правка начнёт свою.
  const cut = () => {
    if (hunk.length) hunks.push(hunk)

    hunk = []
  }

  let i = 0
  let j = 0

  while (i < source.length && j < target.length) {
    if (source[i] === target[j]) {
      cut()
      i++
      j++

      continue
    }

    if (at(i + 1, j) >= at(i, j + 1)) {
      hunk.push({ added: false, text: source[i] ?? '' })
      i++
    } else {
      hunk.push({ added: true, text: target[j] ?? '' })
      j++
    }
  }

  for (; i < source.length; i++) hunk.push({ added: false, text: source[i] ?? '' })
  for (; j < target.length; j++) hunk.push({ added: true, text: target[j] ?? '' })

  cut()

  return hunks
}

/**
 * Аргументы вызова с правками человека (DEV-1562).
 *
 * Правки накладываются на исходный объект, а не собираются заново: у задачи
 * есть поля, которых нет в таблице — описание, метки, исполнитель. Пересборка
 * потеряла бы их молча, и человек узнал бы об этом по созданной задаче.
 *
 * Строки, которые убрали, просто не попадают в результат. Пустая пачка сюда не
 * доезжает: кнопку подтверждения экран к этому моменту уже погасил.
 */
export const applyEdits = (
  name: string,
  args: unknown,
  issues: DraftIssue[],
  removed: ReadonlySet<number>,
): unknown => {
  const input = asRecord(args) ?? {}

  if (name === 'create_issues_batch') {
    const original = Array.isArray(input.issues) ? input.issues : []

    return {
      ...input,
      issues: original
        .map((item, index) => ({ ...(asRecord(item) ?? {}), ...issues[index] }))
        .filter((_, index) => !removed.has(index)),
    }
  }

  return { ...input, ...issues[0] }
}
